import { FC, Suspense } from 'react';

import {
  APP_ENVS,
  CURRENCIES,
  currentAppEnvironment,
} from '@savgroup-front-common/constants';
import { PaylineProvider } from '@savgroup-front-common/core/atoms/ReactPayline';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { PaymentProvider } from '@savgroup-front-common/types/Payment/PaymentProvider';
import { Kind } from '@/api/Payment/getPaymentSessionQuery';

import { useGetPaymentAccount } from './hooks/useGetPaymentAccount';
import { PaymentAdyen } from './PaymentAdyen/PaymentAdyen';
import { PaymentMonext } from './PaymentMonext/PaymentMonext';
import { PaymentPurse } from './PaymentPurse/PaymentPurse';
import { PaymentStripe } from './PaymentStripe';

interface PaymentContentProps {
  invoice: { id: string; priceWithTax: number; currencyTrigram: CURRENCIES };
  onSubmitSuccess: () => Promise<void>;
  isFormCompleted: boolean;
  claimGroupId: string;
  fileId?: string;
}

export const PaymentContent: FC<PaymentContentProps> = ({
  invoice,
  isFormCompleted,
  onSubmitSuccess,
  claimGroupId,
  fileId,
}) => {
  const { paymentAccountDto } = useGetPaymentAccount({ claimGroupId });

  const paymentProvider = paymentAccountDto?.paymentProvider;

  if (!paymentProvider || !invoice) {
    return null;
  }

  return (
    <Suspense fallback={<BaseLoader />}>
      {(paymentProvider === PaymentProvider.PURSE ||
        paymentProvider === PaymentProvider.CUSTOM) && (
        <PaymentPurse
          invoice={invoice}
          onSubmitSuccess={onSubmitSuccess}
          claimGroupId={claimGroupId}
          kindId={fileId ?? claimGroupId}
          kind={fileId ? Kind.FILE : Kind.CLAIM_GROUP}
          isFormCompleted={isFormCompleted}
        />
      )}

      {paymentProvider === PaymentProvider.STRIPE && (
        <PaymentStripe
          onSubmitSuccess={onSubmitSuccess}
          isFormCompleted={isFormCompleted}
          invoice={invoice}
          claimGroupId={claimGroupId}
        />
      )}

      {paymentProvider === PaymentProvider.ADYEN && (
        <PaymentAdyen
          onSubmitSuccess={onSubmitSuccess}
          isFormCompleted={isFormCompleted}
          invoice={invoice}
          claimGroupId={claimGroupId}
        />
      )}

      {paymentProvider === PaymentProvider.MONEXT && (
        <PaylineProvider production={currentAppEnvironment === APP_ENVS.PROD}>
          <PaymentMonext
            invoice={invoice}
            onSubmitSuccess={onSubmitSuccess}
            kindId={fileId ?? claimGroupId}
            kind={fileId ? Kind.FILE : Kind.CLAIM_GROUP}
            isFormCompleted={isFormCompleted}
          />
        </PaylineProvider>
      )}
    </Suspense>
  );
};
